import React from 'react';

export type OneBrainDirection = 'left' | 'right' | 'up' | 'down';

export interface OneBrainTeamState {
  x: number;
  y: number;
  finished: boolean;
  moves: number;
}

interface OneBrainMazeProps {
  maze: string[];
  teamStates?: Record<'A' | 'B', OneBrainTeamState | null>;
  /** Highlight only this team (player phone). Host shows both. */
  focusTeam?: 'A' | 'B';
  compact?: boolean;
}

const TEAM_COLORS: Record<'A' | 'B', string> = {
  A: '#3B82F6',
  B: '#EF4444',
};

/**
 * Shared maze grid for host TV and player phones.
 */
export const OneBrainMaze: React.FC<OneBrainMazeProps> = ({
  maze,
  teamStates,
  focusTeam,
  compact = false,
}) => {
  const rows = maze.length;
  const cols = maze[0]?.length ?? 0;
  const cellSize = compact ? 'clamp(1.1rem, 6vw, 1.6rem)' : 'clamp(1.4rem, 3.2vw, 2.4rem)';

  const avatarAt = (x: number, y: number): ('A' | 'B')[] => {
    const here: ('A' | 'B')[] = [];
    for (const teamId of ['A', 'B'] as const) {
      if (focusTeam && teamId !== focusTeam) continue;
      const st = teamStates?.[teamId];
      if (st && st.x === x && st.y === y) here.push(teamId);
    }
    return here;
  };

  return (
    <div
      style={{
        display: 'inline-grid',
        gridTemplateColumns: `repeat(${cols}, ${cellSize})`,
        gridTemplateRows: `repeat(${rows}, ${cellSize})`,
        gap: 2,
        padding: compact ? '0.5rem' : '0.75rem',
        background: 'rgba(15, 23, 42, 0.65)',
        borderRadius: 16,
        border: '2px solid rgba(var(--brand-primary-rgb, 249, 115, 22), 0.35)',
        maxWidth: '100%',
        overflow: 'auto',
      }}
      aria-label="Maze"
    >
      {maze.map((row, y) =>
        row.split('').map((cell, x) => {
          const isWall = cell === '#';
          const isExit = cell === 'E';
          const avatars = avatarAt(x, y);

          return (
            <div
              key={`${x}-${y}`}
              style={{
                width: cellSize,
                height: cellSize,
                borderRadius: 4,
                background: isWall
                  ? 'rgba(71, 85, 105, 0.95)'
                  : isExit
                    ? 'rgba(34, 197, 94, 0.35)'
                    : 'rgba(30, 41, 59, 0.9)',
                border: isExit ? '2px solid #22C55E' : '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                fontSize: compact ? '0.7rem' : '0.85rem',
                fontWeight: 800,
              }}
            >
              {isExit && avatars.length === 0 && (
                <span style={{ opacity: 0.85 }} aria-hidden>
                  🏁
                </span>
              )}
              {avatars.map((teamId, i) => (
                <span
                  key={teamId}
                  style={{
                    position: avatars.length > 1 ? 'absolute' : 'static',
                    left: avatars.length > 1 ? `${10 + i * 28}%` : undefined,
                    width: '72%',
                    height: '72%',
                    borderRadius: '50%',
                    background: TEAM_COLORS[teamId],
                    border: '2px solid rgba(255,255,255,0.85)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '0.65em',
                  }}
                >
                  {teamId}
                </span>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
};
